/**
 * Fitness Questionnaire Service — the answers that build a personalised plan.
 *
 * Not a `CrudService`: there is one questionnaire per account, read back as a
 * form template and replaced wholesale on submit.
 *
 * - `GET  /fitness-questionnaire?template=true` — the saved answers, shaped to
 *   pre-fill the form. A 404 means none were ever saved: a blank form, not an
 *   error.
 * - `POST /fitness-questionnaire` — save the answers. Plan generation is
 *   queued after the response, so the reply carries no job id; callers follow
 *   progress by job type.
 */

import { apiClient } from './api/client';
import { rethrowTyped } from './api/errors';
import { HTTP_STATUS } from '../config/constants';

import type { SavedQuestionnaireAnswers } from '../utils/questionnaire/questionnaire-form';
import type { FitnessQuestionnaireInput } from '../validation/fitness-questionnaire.schemas';

export interface SubmitQuestionnaireOptions {
  /**
   * Build a new plan from these answers. Left out, the API's own default
   * applies (the questionnaire's first submit generates one).
   */
  generatePersonalizedPlan?: boolean;
}

/** The HTTP status off an axios-style or typed error, in either shape. */
function statusOf(error: unknown): number | undefined {
  if (typeof error === 'object' && error !== null) {
    const e = error as { response?: { status?: number }; status?: number };
    return e.response?.status ?? e.status;
  }
  return undefined;
}

export class FitnessQuestionnaireService {
  /** The saved answers, or `null` when the account has never submitted. */
  async getSaved(): Promise<SavedQuestionnaireAnswers | null> {
    try {
      const response = await apiClient.get<{ data: SavedQuestionnaireAnswers }>(
        '/fitness-questionnaire',
        { params: { template: true } }
      );
      return response.data?.data ?? null;
    } catch (error) {
      if (statusOf(error) === HTTP_STATUS.NOT_FOUND) return null;
      rethrowTyped(error, 'Could not load your saved answers.');
    }
  }

  /** Save the answers. */
  async submit(
    answers: FitnessQuestionnaireInput,
    { generatePersonalizedPlan }: SubmitQuestionnaireOptions = {}
  ): Promise<void> {
    try {
      await apiClient.post(
        '/fitness-questionnaire',
        generatePersonalizedPlan === undefined
          ? answers
          : { ...answers, generate_personalized_plan: generatePersonalizedPlan }
      );
    } catch (error) {
      rethrowTyped(error, 'Something went wrong. Please try again.');
    }
  }
}

export const fitnessQuestionnaireService = new FitnessQuestionnaireService();
